import { useEffect, useState } from 'react';

interface PipelineViewerProps {
  /** Public path to an n8n workflow JSON file. */
  src: string;
  /** Display label shown above the canvas. */
  name?: string;
}

/**
 * Fetches an n8n workflow JSON file and renders it with the <n8n-demo> web
 * component (registered globally by the script in public/index.html). The
 * component takes the workflow as an inline string, so the .json files stay
 * static assets served from public/ rather than being bundled.
 *
 * `clicktointeract` keeps the pan/zoom canvas from hijacking the bottom sheet's
 * scroll and drag-to-dismiss until the user deliberately clicks in.
 */
const PipelineViewer = ({ src, name }: PipelineViewerProps) => {
  const [workflow, setWorkflow] = useState('');
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>(
    'loading',
  );

  useEffect(() => {
    let active = true;
    setStatus('loading');

    (async () => {
      try {
        const res = await fetch(src, { cache: 'no-cache' });
        if (!res.ok) throw new Error(`Failed to load pipeline: ${res.status}`);
        const text = await res.text();
        if (active) {
          setWorkflow(text);
          setStatus('ready');
        }
      } catch {
        if (active) setStatus('error');
      }
    })();

    return () => {
      active = false;
    };
  }, [src]);

  return (
    <div className="flex flex-col gap-3">
      {name && (
        <h3 className="text-base sm:text-lg font-medium text-gray-900">
          {name}
        </h3>
      )}
      {status === 'loading' && (
        <div className="text-sm text-blackSecondary">Loading pipeline…</div>
      )}
      {status === 'error' && (
        <p className="text-sm text-blackSecondary">Couldn't load the pipeline.</p>
      )}
      {status === 'ready' && (
        <div className="overflow-hidden rounded-xl">
          {/* <n8n-demo> is a custom element whose :host defaults to display:inline
              and collapses to 0 height; an inline style (beats the :host rule)
              forces block layout so it fills the row. It draws its own frame and
              renders at a fixed internal height. */}
          <n8n-demo
            workflow={workflow}
            frame="true"
            clicktointeract="true"
            style={{ display: 'block', width: '100%' }}
          />
        </div>
      )}
    </div>
  );
};

export default PipelineViewer;
